import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface City {
  id: string;
  name_uk: string;
  name_en: string;
  region: string;
  latitude: number;
  longitude: number;
  is_popular: boolean;
}

interface LocationResponse {
  city_name: string;
  country: string;
  region?: string;
  coordinates: Coordinates;
  cached: boolean;
}

interface CitiesListResponse {
  popular: City[];
  all: City[];
}

interface LocationContextType {
  currentCity: string | null;
  currentCityData: City | null;
  isLoading: boolean;
  error: string | null;
  cities: CitiesListResponse | null;
  isLocationReady: boolean;
  
  // Methods
  getCurrentCity: () => Promise<string | null>;
  setCurrentCity: (city: string) => Promise<void>;
  detectByGeolocation: () => Promise<string | null>;
  findNearestCity: (coordinates: Coordinates) => Promise<City | null>;
  updateUserCity: (cityId: string) => Promise<void>;
  searchCities: (query: string) => Promise<City[]>;
  loadCities: () => Promise<void>;
  
  // Settings
  autoDetectEnabled: boolean;
  setAutoDetectEnabled: (enabled: boolean) => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

interface LocationProviderProps {
  children: ReactNode;
}

const STORAGE_KEYS = {
  CURRENT_CITY: 'visit_current_city',
  CURRENT_CITY_DATA: 'visit_current_city_data',
  AUTO_DETECT: 'visit_auto_detect_location',
  LAST_COORDINATES: 'visit_last_coordinates',
};

const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const [currentCity, setCurrentCityState] = useState<string | null>(null);
  const [currentCityData, setCurrentCityData] = useState<City | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cities, setCities] = useState<CitiesListResponse | null>(null);
  const [autoDetectEnabled, setAutoDetectEnabledState] = useState(true);
  const [isLocationReady, setIsLocationReady] = useState(false);
  const isFetchingCities = useRef(false);
  const isFetchingLocation = useRef(false);

  // Initialize from localStorage
  useEffect(() => {
    const savedCity = localStorage.getItem(STORAGE_KEYS.CURRENT_CITY);
    const savedCityData = localStorage.getItem(STORAGE_KEYS.CURRENT_CITY_DATA);
    const savedAutoDetect = localStorage.getItem(STORAGE_KEYS.AUTO_DETECT);

    const initializeLocation = async () => {
      if (savedCity) {
        setCurrentCityState(savedCity);
        setIsLocationReady(true);
      } else {
        // Set default city to Kyiv if no saved city
        setCurrentCityState('Київ');
        localStorage.setItem(STORAGE_KEYS.CURRENT_CITY, 'Київ');
        setIsLocationReady(true);
      }

      if (savedCityData) {
        try {
          setCurrentCityData(JSON.parse(savedCityData));
        } catch (e) {
          console.error('Error parsing saved city data:', e);
        }
      }

      if (savedAutoDetect !== null) {
        setAutoDetectEnabledState(savedAutoDetect === 'true');
      }

      // Load cities list on init
      await loadCities();

      // Auto-detect location if enabled and no saved city
      if ((!savedCity || autoDetectEnabled) && savedAutoDetect !== 'false') {
        try {
          await detectByGeolocation();
        } catch (err) {
          console.warn('Auto geolocation failed:', err);
          // Still mark as ready even if geolocation fails
          setIsLocationReady(true);
        }
      }
    };

    initializeLocation();
  }, []);

  const setAutoDetectEnabled = (enabled: boolean) => {
    setAutoDetectEnabledState(enabled);
    localStorage.setItem(STORAGE_KEYS.AUTO_DETECT, enabled.toString());
  };

  const apiCall = async (endpoint: string, options?: RequestInit) => {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string> || {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/v1${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const getCurrentCity = async (): Promise<string | null> => {
    if (currentCity) {
      return currentCity;
    }

    // Try to get from user profile if authenticated
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const cityData = await apiCall('/user/city');
        setCurrentCityState(cityData.name_uk);
        setCurrentCityData(cityData);
        localStorage.setItem(STORAGE_KEYS.CURRENT_CITY, cityData.name_uk);
        localStorage.setItem(STORAGE_KEYS.CURRENT_CITY_DATA, JSON.stringify(cityData));
        return cityData.name_uk;
      } catch (err) {
        console.warn('Failed to get user city from profile:', err);
      }
    }

    // Fallback to geolocation or localStorage
    const geoCity = await detectByGeolocation();
    return geoCity || 'Київ';
  };

  const setCurrentCity = async (cityName: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Find city data by name
      if (!cities) {
        await loadCities();
      }

      const cityData = cities?.all.find(
        city => city.name_uk.toLowerCase() === cityName.toLowerCase() ||
               city.name_en.toLowerCase() === cityName.toLowerCase()
      );

      if (!cityData) {
        throw new Error('Місто не знайдено');
      }

      // Update local state
      setCurrentCityState(cityName);
      setCurrentCityData(cityData);
      setIsLocationReady(true);

      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.CURRENT_CITY, cityName);
      localStorage.setItem(STORAGE_KEYS.CURRENT_CITY_DATA, JSON.stringify(cityData));

      // Update user profile if authenticated
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          await apiCall('/user/city', {
            method: 'PATCH',
            body: JSON.stringify({ city_id: cityData.id }),
          });
        } catch (err) {
          console.warn('Failed to update user city in profile:', err);
          // Don't throw error - local update is still successful
        }
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Помилка оновлення міста');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const detectByGeolocation = async (): Promise<string | null> => {
    if (isFetchingLocation.current) {
      console.log('Skipping geolocation detection: already fetching');
      return currentCity;
    }

    isFetchingLocation.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Check if geolocation is available
      if (!navigator.geolocation) {
        throw new Error('Геолокація не підтримується браузером');
      }

      // Get current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          (error) => {
            // Handle specific geolocation errors
            if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
              // Check if it's a permissions policy issue
              if (error.message.includes('permissions policy')) {
                reject(new Error('Геолокація вимкнена налаштуваннями безпеки браузера'));
              } else {
                reject(new Error('Доступ до геолокації заборонено'));
              }
            } else if (error.code === GeolocationPositionError.POSITION_UNAVAILABLE) {
              reject(new Error('Не вдалося визначити місцезнаходження'));
            } else if (error.code === GeolocationPositionError.TIMEOUT) {
              reject(new Error('Час очікування геолокації вичерпано'));
            } else {
              reject(new Error('Помилка геолокації: ' + error.message));
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000, // 5 minutes cache
          }
        );
      });

      const { latitude, longitude } = position.coords;

      // Save coordinates for later use
      localStorage.setItem(STORAGE_KEYS.LAST_COORDINATES, JSON.stringify({ latitude, longitude }));

      // Get city name from coordinates
      const locationData: LocationResponse = await apiCall(
        `/geocode/reverse?lat=${latitude}&lng=${longitude}`
      );

      const cityName = locationData.city_name;

      // Try to find corresponding city in our database
      const nearestCity = await findNearestCity({ latitude, longitude });
      
      if (nearestCity) {
        setCurrentCityState(nearestCity.name_uk);
        setCurrentCityData(nearestCity);
        localStorage.setItem(STORAGE_KEYS.CURRENT_CITY, nearestCity.name_uk);
        localStorage.setItem(STORAGE_KEYS.CURRENT_CITY_DATA, JSON.stringify(nearestCity));
        return nearestCity.name_uk;
      } else {
        // Fallback to detected city name
        setCurrentCityState(cityName);
        localStorage.setItem(STORAGE_KEYS.CURRENT_CITY, cityName);
        return cityName;
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Помилка визначення геолокації';
      setError(errorMessage);
      console.error('Geolocation detection failed:', err);
      
      // Return cached city or default to Kyiv
      if (currentCity) {
        setIsLocationReady(true);
        return currentCity;
      } else {
        // Set Kyiv as fallback
        setCurrentCityState('Київ');
        localStorage.setItem(STORAGE_KEYS.CURRENT_CITY, 'Київ');
        setIsLocationReady(true);
        return 'Київ';
      }
    } finally {
      isFetchingLocation.current = false;
      setIsLoading(false);
    }
  };

  const findNearestCity = async (coordinates: Coordinates): Promise<City | null> => {
    try {
      const cityData: City = await apiCall(
        `/cities/nearest?lat=${coordinates.latitude}&lng=${coordinates.longitude}`
      );
      return cityData;
    } catch (err) {
      console.warn('Failed to find nearest city:', err);
      return null;
    }
  };

  const updateUserCity = async (cityId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await apiCall('/user/city', {
        method: 'PATCH',
        body: JSON.stringify({ city_id: cityId }),
      });

      // Reload user city data
      const cityData = await apiCall('/user/city');
      setCurrentCityState(cityData.name_uk);
      setCurrentCityData(cityData);
      localStorage.setItem(STORAGE_KEYS.CURRENT_CITY, cityData.name_uk);
      localStorage.setItem(STORAGE_KEYS.CURRENT_CITY_DATA, JSON.stringify(cityData));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Помилка оновлення міста';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const searchCities = async (query: string): Promise<City[]> => {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const results: City[] = await apiCall(`/cities/search?q=${encodeURIComponent(query)}`);
      return results;
    } catch (err) {
      console.error('City search failed:', err);
      return [];
    }
  };

  const loadCities = async (): Promise<void> => {
    if (isFetchingCities.current || cities) {
      // console.log('Skipping loadCities: already fetching or cities loaded');
      return;
    }

    isFetchingCities.current = true;
    try {
      const citiesData: CitiesListResponse = await apiCall('/cities');
      setCities(citiesData);
    } catch (err) {
      console.error('Failed to load cities:', err);
      setError('Помилка завантаження списку міст');
    } finally {
      isFetchingCities.current = false;
    }
  };

  const value: LocationContextType = {
    currentCity,
    currentCityData,
    isLoading,
    error,
    cities,
    isLocationReady,
    getCurrentCity,
    setCurrentCity,
    detectByGeolocation,
    findNearestCity,
    updateUserCity,
    searchCities,
    loadCities,
    autoDetectEnabled,
    setAutoDetectEnabled,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export { LocationProvider };
